/**
 * Action bus — maps VisionEvent → ActionBinding → side effects.
 * All actions are browser-safe (no OS control, no install).
 */

import type { ActionBinding, ActionThen, VisionEvent } from "../engines/types";
import { getCustomOutput } from "./customOutputsStore";
import { DEFAULT_IMAGE_PROMPT, DEFAULT_VIDEO_PROMPT } from "./actionCatalog";

export type ActionLogEntry = {
  id: string;
  t: number;
  bindingId: string;
  summary: string;
  ok: boolean;
};

const FORGE_URLS: Record<string, string> = {
  imageforge: "https://imageforge.forgesuiteui.app",
  videoforge: "https://videoforge.forgesuiteui.app",
  voiceforge: "https://voiceforge.forgesuiteui.app",
};

const LOCAL_FORGE_URLS: Record<string, string> = {
  imageforge: "http://127.0.0.1:5188",
  videoforge: "http://127.0.0.1:5189",
  voiceforge: "http://127.0.0.1:5190",
};

function forgeProductUrl(product: string): string {
  const host = window.location.hostname.toLowerCase();
  const local = host === "localhost" || host === "127.0.0.1";
  if (local && LOCAL_FORGE_URLS[product]) return LOCAL_FORGE_URLS[product];
  return FORGE_URLS[product] || `https://${product}.forgesuiteui.app`;
}

const listeners = new Set<(e: ActionLogEntry) => void>();
const channelListeners = new Map<string, Set<(payload: unknown) => void>>();

/** Shell registers handlers for app commands (grab_to_ask, toggle_mappings, …). */
export type AppCommandHandler = (
  command: string,
  ctx: { event: VisionEvent; payload?: unknown },
) => Promise<string | void> | string | void;

let appCommandHandler: AppCommandHandler | null = null;

export function setAppCommandHandler(fn: AppCommandHandler | null): void {
  appCommandHandler = fn;
}

/** Cooldown per binding id (ms). */
const lastFire = new Map<string, number>();
const COOLDOWN_MS = 900;

export function onActionLog(fn: (e: ActionLogEntry) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function onChannel(channel: string, fn: (payload: unknown) => void): () => void {
  if (!channelListeners.has(channel)) channelListeners.set(channel, new Set());
  channelListeners.get(channel)!.add(fn);
  return () => channelListeners.get(channel)?.delete(fn);
}

function log(entry: ActionLogEntry) {
  for (const fn of listeners) {
    try {
      fn(entry);
    } catch {
      /* ignore */
    }
  }
}

function gestureName(event: VisionEvent): string {
  return event.type === "gesture" ? event.name : "event";
}

/** Expand {{gesture}} {{gesture_label}} {{confidence}} {{time}} in custom text/URLs. */
export function expandTemplate(template: string, event: VisionEvent): string {
  const g = gestureName(event);
  const conf = event.type === "gesture" ? event.confidence : 0;
  return template
    .replace(/\{\{\s*gesture_label\s*\}\}/gi, g.replace(/_/g, " "))
    .replace(/\{\{\s*gesture\s*\}\}/gi, g)
    .replace(/\{\{\s*confidence\s*\}\}/gi, String(Math.round(conf * 100)))
    .replace(/\{\{\s*time\s*\}\}/gi, new Date().toLocaleTimeString());
}

function dispatchHotkey(keys: string): void {
  const parts = keys.split("+").map((s) => s.trim());
  const key = parts[parts.length - 1] || keys;
  const opts: KeyboardEventInit = {
    key,
    code: key.length === 1 ? `Key${key.toUpperCase()}` : key,
    bubbles: true,
    cancelable: true,
    ctrlKey: parts.some((p) => /^ctrl$/i.test(p)),
    altKey: parts.some((p) => /^alt$/i.test(p)),
    shiftKey: parts.some((p) => /^shift$/i.test(p)),
    metaKey: parts.some((p) => /^meta|cmd$/i.test(p)),
  };
  window.dispatchEvent(new KeyboardEvent("keydown", opts));
  document.dispatchEvent(new KeyboardEvent("keydown", opts));
  window.dispatchEvent(new KeyboardEvent("keyup", opts));
}

function playBeep(frequency = 880): void {
  const AC =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) throw new Error("Web Audio not available");
  const ctx = new AC();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = frequency;
  gain.gain.value = 0.08;
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start();
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
  osc.stop(ctx.currentTime + 0.2);
  osc.onended = () => {
    void ctx.close();
  };
}

function speak(text: string): void {
  if (!window.speechSynthesis) throw new Error("speechSynthesis not available");
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text.replace(/_/g, " "));
  u.rate = 1.05;
  u.volume = 0.9;
  window.speechSynthesis.speak(u);
}

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  ta.remove();
}

async function runThen(
  binding: ActionBinding,
  event: VisionEvent,
  depth = 0,
): Promise<{ ok: boolean; summary: string }> {
  if (depth > 8) return { ok: false, summary: "custom depth limit" };
  const t: ActionThen = binding.then;
  const g = gestureName(event);

  switch (t.kind) {
    case "none":
      return { ok: true, summary: "none" };

    case "toast": {
      const message = expandTemplate(t.message || "{{gesture_label}}", event);
      window.dispatchEvent(
        new CustomEvent("visionforge-toast", { detail: { message, gesture: g } }),
      );
      return { ok: true, summary: `toast “${message}”` };
    }

    case "beep": {
      playBeep(t.frequency ?? 880);
      return { ok: true, summary: "beep" };
    }

    case "speak": {
      const text = expandTemplate(t.text || "{{gesture_label}}", event);
      speak(text);
      return { ok: true, summary: `speak “${text}”` };
    }

    case "copy_text": {
      const text = expandTemplate(t.text || "{{gesture}}", event);
      await copyText(text);
      window.dispatchEvent(
        new CustomEvent("visionforge-toast", {
          detail: { message: `Copied “${text}”`, gesture: g },
        }),
      );
      return { ok: true, summary: `copied “${text}”` };
    }

    case "scroll": {
      window.scrollBy({ top: t.dy, left: 0, behavior: "smooth" });
      return { ok: true, summary: `scroll ${t.dy > 0 ? "down" : "up"}` };
    }

    case "history": {
      if (t.dir === "back") window.history.back();
      else window.history.forward();
      return { ok: true, summary: `history ${t.dir}` };
    }

    case "fullscreen": {
      const el =
        document.querySelector(".vf-stage") ||
        document.documentElement;
      if (!document.fullscreenElement) {
        await (el as HTMLElement).requestFullscreen?.();
        return { ok: true, summary: "fullscreen on" };
      }
      await document.exitFullscreen?.();
      return { ok: true, summary: "fullscreen off" };
    }

    case "hotkey": {
      dispatchHotkey(t.keys);
      return { ok: true, summary: `hotkey ${t.keys}` };
    }

    case "open_url": {
      const url = expandTemplate(t.url, event);
      window.open(url, "_blank", "noopener,noreferrer");
      return { ok: true, summary: `open ${url}` };
    }

    case "forge_handoff": {
      const base = forgeProductUrl(t.product);
      const u = new URL(base);
      const payload: Record<string, string> = {
        from: "visionforge",
        gesture: g,
      };
      if (t.params) {
        for (const [k, v] of Object.entries(t.params)) {
          if (v == null || v === "") continue;
          payload[k] = expandTemplate(String(v), event);
        }
      }
      if (t.product === "videoforge" || t.product === "imageforge") {
        if (!payload.prompt) {
          const fallback =
            t.product === "imageforge" ? DEFAULT_IMAGE_PROMPT : DEFAULT_VIDEO_PROMPT;
          payload.prompt = expandTemplate(fallback, event);
        }
        if (!payload.autogen) payload.autogen = "1";
        if (!payload.provider) payload.provider = "any";
        if (!payload.pick) payload.pick = "random";
        if (!payload.rating) payload.rating = "green";
      }
      for (const [k, v] of Object.entries(payload)) {
        u.searchParams.set(k, v);
      }
      // Hash survives ImageForge's /app redirect and is not sent to WAF.
      u.hash = "handoff=" + encodeURIComponent(JSON.stringify(payload));
      window.open(u.toString(), "_blank", "noopener,noreferrer");
      const auto = u.searchParams.get("autogen") !== "0";
      return {
        ok: true,
        summary:
          (t.product === "videoforge" || t.product === "imageforge") && auto
            ? `open ${t.product} · generate`
            : `open ${t.product}`,
      };
    }

    case "app": {
      if (!appCommandHandler) {
        return { ok: false, summary: `app:${t.command} (no shell handler)` };
      }
      const out = await appCommandHandler(t.command, { event, payload: t.payload });
      return { ok: true, summary: out || `app:${t.command}` };
    }

    case "emit": {
      const channel = expandTemplate(t.channel, event);
      const set = channelListeners.get(channel);
      if (set) {
        for (const fn of set) fn(t.payload);
      }
      window.dispatchEvent(
        new CustomEvent("visionforge-emit", {
          detail: { channel, payload: t.payload, gesture: g },
        }),
      );
      if (channel === "visionforge.pause" || channel === "visionforge.mute") {
        window.dispatchEvent(
          new CustomEvent("visionforge-toast", {
            detail: { message: "Mappings paused (legacy emit)", gesture: g },
          }),
        );
      }
      return { ok: true, summary: `emit ${channel}` };
    }

    case "mouse": {
      window.dispatchEvent(
        new CustomEvent("visionforge-mouse", {
          detail: { action: t.action, x: t.x, y: t.y },
        }),
      );
      return {
        ok: true,
        summary: `mouse ${t.action} (in-app only)`,
      };
    }

    case "sequence": {
      if (!t.steps?.length) return { ok: true, summary: t.label || "empty custom" };
      const parts: string[] = [];
      for (const step of t.steps) {
        if (step.kind === "sequence" || step.kind === "custom_ref") {
          parts.push("skipped nested");
          continue;
        }
        const r = await runThen({ ...binding, then: step }, event, depth + 1);
        parts.push(r.summary);
        if (!r.ok) {
          return {
            ok: false,
            summary: `${t.label || "custom"}: ${parts.join(" · ")}`,
          };
        }
      }
      return {
        ok: true,
        summary: t.label ? `${t.label} (${parts.join(" · ")})` : parts.join(" · "),
      };
    }

    case "custom_ref": {
      const recipe = getCustomOutput(t.outputId);
      if (!recipe) {
        return { ok: false, summary: `recipe missing (${t.outputId})` };
      }
      return runThen(
        {
          ...binding,
          then: { kind: "sequence", steps: recipe.steps, label: recipe.name },
          fireWhenPaused: binding.fireWhenPaused || recipe.fireWhenPaused,
        },
        event,
        depth + 1,
      );
    }

    default:
      return { ok: false, summary: "unknown action" };
  }
}

function bindingFiresWhenPaused(b: ActionBinding): boolean {
  if (b.fireWhenPaused) return true;
  if (b.then.kind === "app" && b.then.command === "toggle_mappings") return true;
  if (b.then.kind === "sequence") {
    return b.then.steps.some(
      (s) => s.kind === "app" && s.command === "toggle_mappings",
    );
  }
  if (b.then.kind === "custom_ref") {
    const recipe = getCustomOutput(b.then.outputId);
    return !!recipe?.fireWhenPaused;
  }
  return false;
}

export async function handleVisionEvent(
  event: VisionEvent,
  bindings: ActionBinding[],
  opts?: { mappingsPaused?: boolean },
): Promise<void> {
  if (event.type !== "gesture") return;
  const now = Date.now();
  const paused = !!opts?.mappingsPaused;

  for (const b of bindings) {
    if (!b.enabled) continue;
    if (paused && !bindingFiresWhenPaused(b)) continue;
    if (b.when.engine !== "gesture" && b.when.engine !== event.type) continue;
    if (b.when.gesture && b.when.gesture !== event.name) continue;
    const min = b.when.minConfidence ?? 0.5;
    if (event.confidence < min) continue;

    const last = lastFire.get(b.id) || 0;
    if (now - last < COOLDOWN_MS) continue;
    lastFire.set(b.id, now);

    try {
      const result = await runThen(b, event);
      log({
        id: `${now}-${b.id}`,
        t: now,
        bindingId: b.id,
        summary: `${event.name} → ${result.summary}`,
        ok: result.ok,
      });
    } catch (e) {
      log({
        id: `${now}-${b.id}`,
        t: now,
        bindingId: b.id,
        summary: e instanceof Error ? e.message : String(e),
        ok: false,
      });
    }
  }
}
